/**
 * Device-local message draft held while Variant B waits for email verification.
 *
 * The account and Sendbird channel scopes prevent a draft from being restored
 * into another user's session or conversation.
 */
import { sessionStorageOrNull as storage } from '@/apps/verifyEmail/helpers/sessionAccountStorage';

export type PendingMessageDraft = {
  accountId: string;
  channelUrl: string;
  message: string;
};

export const PENDING_MESSAGE_DRAFT_KEY = 'sc_pending_message_draft';

function isValidDraft(value: unknown): value is PendingMessageDraft {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const draft = value as PendingMessageDraft;
  return (
    typeof draft.accountId === 'string' &&
    draft.accountId.length > 0 &&
    typeof draft.channelUrl === 'string' &&
    draft.channelUrl.length > 0 &&
    typeof draft.message === 'string' &&
    draft.message.length > 0
  );
}

/** @returns true when the draft was written. */
export function savePendingMessageDraft(draft: PendingMessageDraft): boolean {
  const session = storage();
  if (!session || !isValidDraft(draft)) {
    return false;
  }

  try {
    session.setItem(PENDING_MESSAGE_DRAFT_KEY, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns a valid draft for the current account.
 * Clears malformed or cross-account data from a previous session.
 */
export function getPendingMessageDraft(currentAccountId: string): PendingMessageDraft | null {
  const session = storage();
  if (!session) {
    return null;
  }

  const raw = session.getItem(PENDING_MESSAGE_DRAFT_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!isValidDraft(parsed) || parsed.accountId !== currentAccountId) {
      clearPendingMessageDraft();
      return null;
    }

    return parsed;
  } catch {
    clearPendingMessageDraft();
    return null;
  }
}

export function clearPendingMessageDraft(): void {
  const session = storage();
  if (!session) {
    return;
  }

  session.removeItem(PENDING_MESSAGE_DRAFT_KEY);
}
